import { useContext, createContext, Dispatch, ReactNode, useReducer, FC } from 'react'
import { CourseConfig } from '../hooks/useCourseConfig'

type LMSState = {
    isLMSAvailable: boolean
    courseConfig: CourseConfig | null
}

const initialState: LMSState = {
    isLMSAvailable: false,
    courseConfig: null
}

enum LMSContextActionType {
    SET_LMS_AVAILABLE,
    SET_COURSE_CONFIG
}

export type LMSContextAction =
    {
        type: LMSContextActionType.SET_LMS_AVAILABLE
        payload: boolean
    } |
    {
        type: LMSContextActionType.SET_COURSE_CONFIG
        payload: CourseConfig | null
    }

const LMSContext = createContext<{
    lmsState: LMSState
    lmsDispatch: Dispatch<LMSContextAction>
}>({ lmsState: initialState, lmsDispatch: () => null })

const lmsReducer = (state: LMSState, action: LMSContextAction): LMSState => {
    switch (action.type) {
        case LMSContextActionType.SET_COURSE_CONFIG:
            return { ...state, courseConfig: action.payload } as LMSState

        case LMSContextActionType.SET_LMS_AVAILABLE:
            return { ...state, isLMSAvailable: action.payload } as LMSState

        default:
            return state
    }
}

const LMSContextProvider: FC<{ children: ReactNode }> = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(lmsReducer, initialState)
    const contextValue = { lmsState: state, lmsDispatch: dispatch }
    return (
        <LMSContext.Provider value={contextValue}>
            {children}
        </LMSContext.Provider>
    )
}

const useLMSContext = () => {
    const context = useContext(LMSContext)
    if (!context) throw new Error(
        `useLMSContext must be used within a LMSContextProvider.
        Wrap a parent component in <LMSContextProvider> to fix this error.`
    )
    return context
}

export { useLMSContext, LMSContextProvider, LMSContextActionType }
